import {
  Alert,
  Button,
  Flex,
  Grid,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import {
  PiCheck,
  PiCloudArrowDown,
  PiEye,
  PiPencil,
  PiPlus,
  PiTrash,
  PiX,
} from 'react-icons/pi';
import { useLocation, useParams } from 'wouter';
import CopyInput from '@/components/common/CopyInput';
import DateSinceDisplay from '@/components/common/DateSinceDisplay';
import StickyWrapper from '@/components/common/StickyWrapper';
import ContentBlockSelector from '@/components/content-blocks/ContentBlockSelector';
import { CURRENCY_OPTIONS } from '@/lib/constants';
import {
  SplitPlansAffectedError,
  useCommercialProposal,
  useCommercialProposalsBlocks,
  useDeleteCommercialProposal,
  usePublishCommercialProposal,
  useSaveCommercialProposal,
  useUnpublishCommercialProposal,
} from '@/models/commercial-proposal';
import {
  BlockTypes,
  ITitlePageBlock,
  initializeNewBlock,
  useBlocksActions,
  useBlocksStore,
} from '@/models/commercial-proposals/blocks.store';
import { useProposalActions } from '@/models/commercial-proposals/proposal.store';
import BlocksView from './blocks/BlocksView';
import DisclaimerSection from './blocks/DisclaimerSection';
import RenewableServicesTable from './blocks/RenewableServicesTable';
import { useProposalAccess } from '@/models/permissions';
import ShopPanel from './ShopPanel';
import { listUrl } from '@/lib/listMemory';

const defaultDisclaimer = `*Please note: Companies in this jurisdiction are subject to renewals and may have on demand compliance requirements.

*Please note: Requirements may differ depending on the underlying business model as well as the country of origin of participating shareholders and UBOs. Once the process begins, your account manager will guide you through the process and share the correct case specific details.

*This commercial proposal is subject to change on a rolling basis and not a legally binding agreement/contract. All services and agreements hereto are subject to the [ToS](https://legalbison.com/terms-of-service/) and [Privacy Policy](https://legalbison.com/privacy-policy). 

*All stated fees are excluding VAT. All figures are in USD {TICKER} ({SYMBOL})`;

export default function CommercialProposalEditPage() {
  const { canSeeShop } = useProposalAccess();
  const { id } = useParams();
  const { data: offer, isLoading } = useCommercialProposal(Number(id));
  const { setProposal } = useProposalActions();
  const saveCommercialProposal = useSaveCommercialProposal(Number(id));
  const deleteCommercialProposal = useDeleteCommercialProposal();
  const { mutate: publish } = usePublishCommercialProposal();
  const { mutate: unpublish } = useUnpublishCommercialProposal();
  const { data: blocksData } = useCommercialProposalsBlocks(Number(id));
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const blocks = useBlocksStore((state) => state.blocks);
  const { add, initialize, clear, select } = useBlocksActions();
  const [, setLocation] = useLocation();
  const [disclaimer, setDisclaimer] = useState('');
  const [renewableServiceOrder, setRenewableServiceOrder] = useState<
    number[] | null
  >(null);

  useEffect(() => {
    if (!blocksData) return;
    if (blocksData.length && offer) {
      if (!blocksData.find(({ type }) => type === BlockTypes.TitlePage)) {
        const { config, ...rest } = initializeNewBlock(
          offer.id,
          BlockTypes.TitlePage,
          0,
        ) as ITitlePageBlock;

        blocksData.unshift({
          ...rest,
          config: {
            ...config,
            jurisdiction: offer.jurisdiction,
            documentTitle: offer.name,
          },
        } satisfies ITitlePageBlock);
      }

      initialize(blocksData);
    }
    return clear;
  }, [offer, blocksData, initialize, clear]);

  /* eslint-disable react-hooks/set-state-in-effect -- seeds editor fields from fetched offer */
  useEffect(() => {
    if (offer) {
      setProposal(offer);
      setEditedName(offer.name);
      setDisclaimer(
        offer.disclaimer ||
          defaultDisclaimer
            .replace('{TICKER}', offer.currency)
            .replace(
              '{SYMBOL}',
              CURRENCY_OPTIONS.find((o) => o.value === offer.currency)
                ?.symbol ?? offer.currency,
            ),
      );
      setRenewableServiceOrder(offer.data?.renewableServiceOrder ?? null);
    }
    return () => {
      setProposal(null);
    };
  }, [offer, setProposal]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSave = (
    includeNameUpdate = false,
    acknowledgedOfferIds?: number[],
  ) => {
    saveCommercialProposal
      .mutateAsync({
        blocks,
        name: editedName,
        disclaimer,
        renewableServiceOrder,
        invalidateSplitPlans: !!acknowledgedOfferIds,
        acknowledgedOfferIds,
      })
      .then(() => {
        notifications.show({
          title: 'Success',
          message: includeNameUpdate
            ? 'Proposal name and blocks saved successfully'
            : 'Commercial proposal saved successfully',
          color: 'green',
          position: 'top-right',
        });
        if (includeNameUpdate) {
          setIsEditingName(false);
        }
      })
      .catch((e) => {
        // Anything else has already been reported by the global mutation handler;
        // rethrowing here would only raise an unhandled rejection on top of it.
        if (!(e instanceof SplitPlansAffectedError)) return;
        // Offers are still selecting from this proposal. Name them before BizDev
        // commits: saving throws away work an SR owns and a customer is waiting on,
        // and the count alone gives no way to judge that or warn anyone.
        modals.openConfirmModal({
          title: 'This will discard split payment plans',
          children: (
            <Stack gap="sm">
              <Text size="sm">{e.message}</Text>
              <Stack gap={6}>
                {e.offers.map((o) => (
                  <Group key={o.offerId} gap={8} wrap="nowrap">
                    <Text size="sm" fw={500}>
                      #{o.offerId} {o.offerTitle ?? '(untitled)'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {[o.contactName, o.accountName]
                        .filter(Boolean)
                        .join(' · ')}
                      {o.planStatus
                        ? ` — plan ${o.planStatus}`
                        : ' — no plan yet'}
                    </Text>
                  </Group>
                ))}
              </Stack>
              <Text size="xs" c="dimmed">
                Accepted and completed offers are not affected. Each offer's
                sales representative will be notified.
              </Text>
            </Stack>
          ),
          labels: { confirm: 'Discard plans and save', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          // Echo back exactly what was listed. If a customer confirms a selection while
          // this dialog is open, the server refuses rather than discarding an offer that
          // was never on screen.
          onConfirm: () =>
            handleSave(
              includeNameUpdate,
              e.offers.map((o) => o.offerId),
            ),
        });
      });
  };

  const shopUsedCount = canSeeShop ? (offer?.shop?.usedOn.length ?? 0) : 0;
  const shopUsageWarning = `This proposal is used on ${shopUsedCount} shop page(s). They will show it as unavailable.`;

  const handleUnpublish = () => {
    const run = () =>
      unpublish(offer!.id, {
        onSuccess: () => {
          notifications.show({
            title: 'Proposal was un-published',
            message: "Now it's on-hold and not possible to send it to clients",
            color: 'green',
            position: 'top-right',
          });
        },
      });
    if (shopUsedCount === 0) {
      run();
      return;
    }
    modals.openConfirmModal({
      title: 'Un-publish Commercial Proposal',
      children: <Text size="sm">{shopUsageWarning}</Text>,
      labels: { confirm: 'Un-publish', cancel: 'Cancel' },
      confirmProps: { color: 'orange' },
      onConfirm: run,
    });
  };

  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Delete Commercial Proposal',
      children: (
        <>
          <p>
            Are you sure you want to delete this commercial proposal? This
            action cannot be undone.
          </p>
          {shopUsedCount > 0 && <p>{shopUsageWarning}</p>}
        </>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteCommercialProposal.mutate(Number(id), {
          onSuccess: () => {
            notifications.show({
              title: 'Success',
              message: `Commercial proposal '${offer!.name}' deleted successfully`,
              color: 'green',
              position: 'top-right',
            });
            setLocation(`~${listUrl('/commercial-proposals')}`);
          },
        });
      },
    });
  };

  if (isLoading) {
    return null;
  }

  if (!offer) {
    return <div>Offer does not exist, or you have no access to it.</div>;
  }

  return (
    <>
      <Grid>
        <Grid.Col span={8} id="left-column">
          <Stack gap={0}>
            {offer.isDraft && (
              <Alert color="green" title="Draft" mb="md">
                This commercial proposal is currently not publishd yet. It's not
                possible to send it to clients unless it's Published.
                <Flex justify="flex-end">
                  <Button
                    color="green"
                    size="xs"
                    variant="light"
                    onClick={() => {
                      publish(offer.id, {
                        onSuccess: () => {
                          notifications.show({
                            title: 'Proposal was published',
                            message:
                              "Now it's available to be sent out ot clients",
                            color: 'green',
                            position: 'top-right',
                          });
                        },
                      });
                    }}
                  >
                    Publish
                  </Button>
                </Flex>
              </Alert>
            )}
            <Group>
              {isEditingName ? (
                <Group gap="xs">
                  <TextInput
                    value={editedName}
                    onChange={(e) => setEditedName(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingName(false);
                      }
                      if (e.key === 'Escape') {
                        setIsEditingName(false);
                        setEditedName(offer?.name || '');
                      }
                    }}
                    size="sm"
                    style={{ width: 300 }}
                  />
                  <Button
                    size="xs"
                    variant="light"
                    color="green"
                    onClick={() => setIsEditingName(false)}
                    loading={saveCommercialProposal.isPending}
                  >
                    <PiCheck size={16} />
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="gray"
                    onClick={() => {
                      setIsEditingName(false);
                      setEditedName(offer?.name || '');
                    }}
                  >
                    <PiX size={16} />
                  </Button>
                </Group>
              ) : (
                <Group gap="xs">
                  <Title order={2} size="sm">
                    {editedName}, {offer?.jurisdiction}, {offer?.currency}
                  </Title>
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => setIsEditingName(true)}
                  >
                    <PiPencil size={16} />
                  </Button>
                </Group>
              )}
            </Group>

            <Group>
              <Group gap={5}>
                <Text size="xs" c="dimmed">
                  Last edited:
                </Text>
                <DateSinceDisplay date={offer.updatedAt} size="xs" c="dimmed" />
              </Group>
              <CopyInput value={`ID: ${offer.id}`} />
            </Group>
          </Stack>

          <BlocksView />
          <RenewableServicesTable
            order={renewableServiceOrder}
            onReorder={setRenewableServiceOrder}
          />
          <DisclaimerSection value={disclaimer} onChange={setDisclaimer} />
        </Grid.Col>

        <Grid.Col span={4} id="right-column">
          <StickyWrapper observeElementId="left-column" topOffset={20}>
            <Group className="no-block-deselect" mb="md">
              <Button
                leftSection={<PiPlus size={16} />}
                size="xs"
                variant="light"
                onClick={() => setAddModalOpen(true)}
              >
                Add Content Block
              </Button>

              <Button
                leftSection={<PiCloudArrowDown size={16} />}
                size="xs"
                variant="light"
                onClick={() => handleSave()}
                loading={saveCommercialProposal.isPending}
              >
                Save
              </Button>

              <Button
                leftSection={<PiEye size={16} />}
                size="xs"
                variant="light"
                onClick={() => {
                  window.open(
                    `/api/backoffice/commercial-proposal/preview?uuid=${offer!.uuid}`,
                    '_blank',
                  );
                }}
              >
                Preview
              </Button>

              <Button
                leftSection={<PiTrash size={16} />}
                size="xs"
                variant="light"
                color="red"
                onClick={handleDelete}
                loading={deleteCommercialProposal.isPending}
              >
                Delete
              </Button>

              {!offer.isDraft && (
                <Button
                  color="orange"
                  size="xs"
                  variant="light"
                  onClick={handleUnpublish}
                >
                  Un-publish
                </Button>
              )}
            </Group>
            {canSeeShop && (
              <ShopPanel
                proposalId={offer.id}
                isDraft={offer.isDraft}
                shop={offer.shop}
              />
            )}
            <div id="settings-portal" />
          </StickyWrapper>
        </Grid.Col>
      </Grid>

      <ContentBlockSelector
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSelect={(type: BlockTypes) => {
          const block = initializeNewBlock(offer.id, type, blocks.length);
          add(block);
          select(block.uuid);
        }}
      />
    </>
  );
}
